import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import styles from "../../assets/scss/Settings/Settings.module.scss";
import dummyProfile from "../../assets/images/Profile.png";
import { BaseURL } from "../../lib/api";
import { getUserInfo } from "../../utils/user.util";
import { useGetUserByIdQuery } from "../../slices/admin/admin";

const Settings = () => {
  const navigate = useNavigate();
  const localUser = getUserInfo();
  const canAccessPrintAndBanner = ["siteadmin", "siteowner", "wholesaleowner"].includes(localUser?.type);
  const userId = localUser?._id;

  const canAccessHomeAndBanner = ["siteadmin", "siteowner"].includes(localUser?.type);


  // Fetch latest user data
  const { data: apiData, refetch } = useGetUserByIdQuery(userId, { skip: !userId });

  // Form states
  const [language, setLanguage] = useState(localStorage.getItem("appLanguage") || "English");
  const [selectedImage, setSelectedImage] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [showLogo, setShowLogo] = useState(localUser?.isPrintLogo ?? true);
  const [printerOption, setPrinterOption] = useState(localUser?.printerOption || "");
  const [showTerms, setShowTerms] = useState("");
  const [termsText, setTermsText] = useState("");

  // Inside component states (add this)
  const [showRS232, setShowRS232] = useState(false);


  // Banner states
  const [bannerImage, setBannerImage] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);

  // Update form when API data arrives
  useEffect(() => {
    if (apiData?.result?.length > 0) {
      const user = apiData.result[0];
      setShowLogo(user.isPrintLogo ?? true);
      setPrinterOption(user.printerOption || "");
      setShowTerms(user.isTnc ? "yes" : "no");
      setTermsText(user.tnc || "");
      setShowRS232(user.isRS232Enabled ?? false);
      // if (user.store?.logo) {
      //   setSelectedImage(`${BaseURL}/${user.store.logo}`);
      // }
      // if (user.bannerImage) {
      //   setBannerImage(`${BaseURL}/${user.bannerImage}`);
      // }
      // 🧠 Auto-handle S3 or local URLs
      if (user.store?.logo) {
        const logoUrl = (typeof user.store.logo === "string" && user.store.logo.startsWith("http"))
          ? user.store.logo
          : `${BaseURL}/${user.store.logo}`;
        setSelectedImage(logoUrl);
      }

      if (user.bannerImage) {
        const bannerUrl = (typeof user.bannerImage === "string" && user.bannerImage.startsWith("http"))
          ? user.bannerImage
          : `${BaseURL}/${user.bannerImage}`;
        setBannerImage(bannerUrl);
      }

    }
  }, [apiData]);

  // Logo upload
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Logo must be below 2MB");
        return;
      }
      setSelectedImage(URL.createObjectURL(file));
      setLogoFile(file);
    }
  };

  // Banner upload
  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Banner must be below 2MB");
        return;
      }
      setBannerImage(URL.createObjectURL(file));
      setBannerFile(file);
    }
  };

  // Save settings API
  const handleSave = async () => {
    try {
      const formData = new FormData();
      formData.append("isPrintLogo", showLogo);
      formData.append("printerOption", printerOption);
      formData.append("isTnc", showTerms === "yes");
      formData.append("tnc", termsText);
      formData.append("isRS232Enabled", showRS232);
      if (logoFile) formData.append("printerLogo", logoFile);
      if (bannerFile) formData.append("bannerImage", bannerFile);

      const rawToken = localStorage.getItem("token");
      const token = rawToken?.replace(/^"|"$/g, "");

      const response = await axios.put(`${BaseURL}/user/update/settings`, formData, {
        headers: {
          authorization: token,
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success(response.data.message || "Settings updated successfully!");
      refetch();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update settings");
    }
  };

  const handleLanguageSave = () => {
    localStorage.setItem("appLanguage", language);

    const languageMap = {
      English: "en",
      Tamil: "ta",
      Telugu: "te",
      Malayalam: "ml",
      Kannada: "kn",
      Hindi: "hi",
    };

    document.body.className = document.body.className.replace(/lang-\w+/g, '');
    if (language !== 'English') {
      document.body.classList.add(`lang-${languageMap[language]}`);
    }

    const select = document.querySelector(".goog-te-combo");
    if (select) {
      select.value = languageMap[language];
      select.dispatchEvent(new Event("change"));
    }

    toast.success("Language preference saved!");
  };

  // 🔹 Fetch banners from API


  const [bannerFiles, setBannerFiles] = useState([]);
  const [bannerPreviews, setBannerPreviews] = useState([]);
  const [existingBanners, setExistingBanners] = useState([]);

  useEffect(() => {
    const fetchLandingBanners = async () => {
      try {
        const rawToken = localStorage.getItem("token");
        const token = rawToken?.replace(/^"|"$/g, "");
        const res = await axios.get(`${BaseURL}/dashboard/getLandingBanners`, {
          headers: { authorization: token },
        });

        const fetched = res?.data?.result?.landingPageImages || [];
        // setExistingBanners(fetched.map((path) => `${BaseURL}/${path}`));
        setExistingBanners(
          res.data.result.landingPageImages.map((p) =>
            (typeof p === "string" && p.startsWith("http")) ? p : `${BaseURL}/${p}`
          )
        );

      } catch (error) {
        console.error("Failed to fetch banners", error);
      }
    };

    fetchLandingBanners();
  }, []);


  const handleBannerChangeHome = (e) => {
    const files = Array.from(e.target.files);

    // No file size restriction now
    const previews = files.map((file) => URL.createObjectURL(file));

    setBannerFiles((prev) => [...prev, ...files]);
    setBannerPreviews((prev) => [...prev, ...previews]);
  };

  // 🔹 Remove existing or new banner
  const handleRemoveBanner = (index, type) => {
    if (type === "existing") {
      setExistingBanners((prev) => prev.filter((_, i) => i !== index));
    } else {
      setBannerFiles((prev) => prev.filter((_, i) => i !== index));
      setBannerPreviews((prev) => prev.filter((_, i) => i !== index));
    }
  };

  // 🔹 Save (upload + update banners)
  const handleBannerSave = async () => {
    try {
      const formData = new FormData();

      // Existing (already uploaded) banners
      existingBanners.forEach((url) => {
        // Strip BaseURL path before sending
        const relativePath = url.replace(`${BaseURL}/`, "");
        formData.append("existingImages", relativePath);
      });

      // New images
      bannerFiles.forEach((file) => formData.append("landingPageImages", file));

      const rawToken = localStorage.getItem("token");
      const token = rawToken?.replace(/^"|"$/g, "");

      const res = await axios.post(`${BaseURL}/dashboard/landingPage`, formData, {
        headers: {
          authorization: token,
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success(res?.data?.message || "Landing page banners updated successfully!");
      setBannerFiles([]);
      setBannerPreviews([]);
      // Refetch updated list
      const updated = await axios.get(`${BaseURL}/dashboard/getLandingBanners`, {
        headers: { authorization: token },
      });
      setExistingBanners(updated?.data?.result?.landingPageImages.map((p) => `${BaseURL}/${p}`));
    } catch (err) {
      console.error(err);
      toast.error("Failed to update landing page banners");
    }
  };



  return (
    <div className={styles.settingsContainer}>
      <div className={styles.headerRow}>
        <h2 className={styles.header}>Settings</h2>
      </div>

      {/* Language */}
      <div className={`${styles.card} notranslate`}>
        <label className={styles.label}>Select Your Language</label>
        <select className={styles.inputs} value={language} onChange={(e) => setLanguage(e.target.value)}>
          <option value="English">English</option>
          <option value="Tamil">Tamil</option>
          <option value="Malayalam">Malayalam</option>
          <option value="Hindi">Hindi</option>
          <option value="Telugu">Telugu</option>
          <option value="Kannada">Kannada</option>
        </select>

        <div className={styles.footerButtons}>
          <button className={styles.cancel} onClick={() => navigate('/dashboard')}>Cancel</button>
          <button className={styles.save} onClick={handleLanguageSave}>Save</button>
        </div>
      </div>

      {/* {canAccessPrintAndBanner && (
        <>
          <div className={styles.card}>
            <h2 className={styles.header}>Thermal Print Settings</h2>
            <div className={styles.dflex}>
              <div>
                <div className={styles.widthcontent}>
                  <label className={styles.label}>Select Printer</label>
                  <select className={styles.inputs} value={printerOption} onChange={(e) => setPrinterOption(e.target.value)}>
                    <option value="">Select Printer</option>
                    <option value="80">Thermal Printer 80mm</option>
                    <option value="58">Thermal Printer 58mm</option>
                  </select>
                </div>

                <div className={styles.widthcontent}>
                  <label className={styles.label}>Enable Terms and Conditions</label>
                  <select className={styles.inputs} value={showTerms} onChange={(e) => setShowTerms(e.target.value)}>
                    <option value="">Select Terms and Conditions</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>

                {showTerms === "yes" && (
                  <div className={styles.textareaContainer}>
                    <label className={styles.label}>Terms and Conditions</label>
                    <textarea
                      className={styles.textarea}
                      value={termsText}
                      onChange={(e) => setTermsText(e.target.value)}
                      placeholder="Type your terms here..."
                      rows={4}
                    />
                  </div>
                )}
              </div>

              <div className={styles.widthcontent}>
                <div className={styles.toggleLogoBox}>
                  <span className={styles.toggleText}>{showLogo ? "Logo Shown" : "Logo Hidden"}</span>
                  <div className={`${styles.toggleSwitch} ${showLogo ? styles.on : styles.off}`} onClick={() => setShowLogo(!showLogo)}>
                    <div className={styles.toggleCircle}></div>
                  </div>
                </div>

                {showLogo && (
                  <div className={styles.imageContainer}>
                    <img src={selectedImage || dummyProfile} alt="Company Logo" className={styles.profileImage} />
                    <div style={{ marginTop: "20px" }}>
                      <label htmlFor="file-upload" className={styles.changeImageButton}>Upload Org Logo</label>
                      <input id="file-upload" type="file" accept="image/png, image/jpeg" onChange={handleImageChange} style={{ display: "none" }} />
                      <p className={styles.imageInfo}>Upload image below 2MB (JPG, PNG)</p>
                    </div>
                  </div>
                )}
              


              </div>


            </div>

            <div className={styles.footerButtons}>
              <button className={styles.cancel}>Cancel</button>
              <button className={styles.save} onClick={handleSave}>Save</button>
            </div>
          </div>

          <div className={styles.card}>
            <h2 className={styles.header}>Banner Settings</h2>
            <div className={styles.imageContainer}>
              <img src={bannerImage || dummyProfile} alt="Banner Preview" className={styles.profileImage} />
              <div style={{ marginTop: "20px" }}>
                <label htmlFor="banner-upload" className={styles.changeImageButton}>Upload Banner Image</label>
                <input id="banner-upload" type="file" accept="image/png, image/jpeg" onChange={handleBannerChange} style={{ display: "none" }} />
                <p className={styles.imageInfo}>Upload banner below 2MB (JPG, PNG)</p>
              </div>
            </div>

            <div className={styles.footerButtons}>
              <button className={styles.cancel}>Cancel</button>
              <button className={styles.save} onClick={handleSave}>Save</button>
            </div>
          </div>

       

          <div className={styles.card}>
            <h2 className={styles.header}>RS-232 Weight Machine Setting</h2>

            <div className={styles.toggleLogoBoxRs}>
              <span className={styles.toggleText}>
                {showRS232
                  ? "RS-232 Weight Machine Setting Shown"
                  : "RS-232 Weight Machine Setting Hidden"}
              </span>
              <div
                className={`${styles.toggleSwitch} ${showRS232 ? styles.on : styles.off}`}
                onClick={() => setShowRS232(!showRS232)}
              >
                <div className={styles.toggleCircle}></div>
              </div>
            </div>

            {showRS232 && (
              <div className={styles.instructionsBox}>
                <h3 className={styles.instructionsHeader}>Setup Instructions</h3>
                <ol className={styles.instructionsList}>
                  <li>
                    Connect the device to your system using the appropriate cable or adapter.
                  </li>
                  <li>
                    Install the compatible driver for the connected device or adapter before use.
                  </li>
                  <li>
                    Once installed, confirm that the device appears correctly in your system’s device list or ports.
                  </li>
                  <li>
                    Use the detected device path or port in your application to communicate or read data.
                  </li>
                </ol>
              </div>
            )}

            <div className={styles.footerButtons}>
              <button className={styles.cancel}>Cancel</button>
              <button className={styles.save} onClick={handleSave}>Save</button>
            </div>
          </div>





        </>
      )}

    
      {canAccessHomeAndBanner && (
        <>
          <div className={styles.card}>
            <h2 className={styles.header}>Home Banner Settings</h2>

            <div className={styles.imageContainerMulti}>
              {existingBanners.length > 0 && (
                <div className={styles.previewGrid}>
                  {existingBanners.map((src, index) => (
                    <div key={`existing-${index}`} className={styles.previewBox}>
                      <img
                        src={src}
                        alt={`Existing Banner ${index + 1}`}
                        className={styles.previewImage}
                      />
                      <button
                        type="button"
                        className={styles.removeButton}
                        onClick={() => handleRemoveBanner(index, "existing")}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {bannerPreviews.length > 0 && (
                <div className={styles.previewGrid}>
                  {bannerPreviews.map((src, index) => (
                    <div key={`new-${index}`} className={styles.previewBox}>
                      <img
                        src={src}
                        alt={`New Banner ${index + 1}`}
                        className={styles.previewImage}
                      />
                      <button
                        type="button"
                        className={styles.removeButton}
                        onClick={() => handleRemoveBanner(index, "new")}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginTop: "20px" }}>
                <label htmlFor="home-banner-upload" className={styles.changeImageButton}>
                  Upload Home Banner Images
                </label>
                <input
                  id="home-banner-upload"
                  type="file"
                  accept="image/png, image/jpeg"
                  multiple
                  onChange={handleBannerChangeHome}
                  style={{ display: "none" }}
                />
                <p className={styles.imageInfo}>
                  You can upload multiple banner images (JPG or PNG).
                </p>
              </div>
            </div>

            <div className={styles.footerButtons}>
              <button
                type="button"
                className={styles.cancel}
                onClick={() => {
                  setBannerFiles([]);
                  setBannerPreviews([]);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.save}
                onClick={handleBannerSave}
              >
                Save
              </button>
            </div>
          </div>
        </>
      )} */}

    </div>
  );
};

export default Settings;

